import React, { useState, useRef } from "react";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Career } from "../Types/Types";
import FormActionButtons from "../Assets/FormActionButtons";
import LanguageTabs, { FormLang } from "../Assets/LanguageTabs";
import { useTranslation } from "react-i18next";

type EnDepartment =
  | "Graphic Design"
  | "R&D"
  | "Pharmaceutical Quality Control"
  | "Pharmaceutical Quality Assurance"
  | "Pharmaceutical Microbiology"
  | "Pharmaceutical Regulatory Affairs"
  | "Maintenance and Engineering"
  | "MANUFACTURE"
  | "Storage"
  | "Sales"
  | "Pharmaceutical Procurement"
  | "Finance"
  | "SECURITY";

const departmentMap = {
  enToMk: {
    "Graphic Design": "Графички дизајн",
    "R&D": "Истражување и развој",
    "Pharmaceutical Quality Control": "Контрола на квалитет",
    "Pharmaceutical Quality Assurance": "Обезбедување на квалитет",
    "Pharmaceutical Microbiology": "Фармацевтска микробиологија",
    "Pharmaceutical Regulatory Affairs": "Регулаторни работи",
    "Maintenance and Engineering": "Одржување и инженеринг",
    "MANUFACTURE": "Производство",
    "Storage": "Магацин",
    "Sales": "Продажба",
    "Pharmaceutical Procurement": "Набавка",
    "Finance": "Финансии",
    "SECURITY": "Обезбедување",
  } as Record<EnDepartment, string>,
  enToSq: {
    "Graphic Design": "Dizajn grafik",
    "R&D": "Hulumtim dhe zhvillim",
    "Pharmaceutical Quality Control": "Kontrolli i cilësisë farmaceutike",
    "Pharmaceutical Quality Assurance": "Sigurimi i cilësisë farmaceutike",
    "Pharmaceutical Microbiology": "Mikrobiologjia farmaceutike",
    "Pharmaceutical Regulatory Affairs": "Çështjet rregullatore farmaceutike",
    "Maintenance and Engineering": "Mirëmbajtja dhe inxhinieria",
    "MANUFACTURE": "Prodhimi",
    "Storage": "Magazina",
    "Sales": "Shitja",
    "Pharmaceutical Procurement": "Furnizimi farmaceutik",
    "Finance": "Financa",
    "SECURITY": "Siguria",
  } as Record<EnDepartment, string>,
  mkToEn: {} as Record<string, EnDepartment>,
  sqToEn: {} as Record<string, EnDepartment>,
};

for (const [en, mk] of Object.entries(departmentMap.enToMk)) {
  departmentMap.mkToEn[mk] = en as EnDepartment;
}
for (const [en, sq] of Object.entries(departmentMap.enToSq)) {
  departmentMap.sqToEn[sq] = en as EnDepartment;
}

interface CareerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (career: Omit<Career, "_id" | "createdAt" | "updatedAt">) => void;
}

type Multi = { en: string; mk: string; sq: string };
const emptyMulti: Multi = { en: "", mk: "", sq: "" };

const CareerModal: React.FC<CareerModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const [activeLang, setActiveLang] = useState<FormLang>("en");
  const [vacancy, setVacancy] = useState<Multi>({ ...emptyMulti });
  const [description, setDescription] = useState<Multi>({ ...emptyMulti });
  const [shortDescription, setShortDescription] = useState<Multi>({ ...emptyMulti });
  const [department, setDepartment] = useState({ en: "Graphic Design" as EnDepartment, mk: "Графички дизајн", sq: "Dizajn grafik" });
  const [active, setActive] = useState({ en: true, mk: true, sq: false });

  const quillRef = useRef<ReactQuill>(null);

  const setField = (
    setter: React.Dispatch<React.SetStateAction<Multi>>,
    value: string
  ) => setter((prev) => ({ ...prev, [activeLang]: value }));

  function isQuillContentEmpty(html: string) {
    const text = html.replace(/<(.|\n)*?>/g, '').replace(/&nbsp;/g, '').trim();
    return text.length === 0;
  }

  const isFormValid = () => {
    return (
      vacancy.en.trim() !== '' &&
      vacancy.mk.trim() !== '' &&
      shortDescription.en.trim() !== '' &&
      shortDescription.mk.trim() !== '' &&
      !isQuillContentEmpty(description.en) &&
      !isQuillContentEmpty(description.mk)
    );
  };

  // Selecting a department in any language fills all three translations.
  const selectDepartmentByEn = (enValue: EnDepartment | "") => {
    setDepartment({
      en: enValue as EnDepartment,
      mk: enValue ? departmentMap.enToMk[enValue] : "",
      sq: enValue ? departmentMap.enToSq[enValue] : "",
    });
  };

  const onDepartmentChange = (value: string) => {
    let enValue: EnDepartment | "" = "";
    if (activeLang === "en") enValue = value as EnDepartment;
    else if (activeLang === "mk") enValue = departmentMap.mkToEn[value] || "";
    else enValue = departmentMap.sqToEn[value] || "";
    selectDepartmentByEn(enValue);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ vacancy, description, shortDescription, department, active });
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setVacancy({ ...emptyMulti });
    setDescription({ ...emptyMulti });
    setShortDescription({ ...emptyMulti });
    setDepartment({ en: "Graphic Design", mk: "Графички дизајн", sq: "Dizajn grafik" });
    setActive({ en: true, mk: true, sq: false });
    setActiveLang("en");
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  const langSuffix = activeLang.toUpperCase();

  // Department dropdown options/value for the active language.
  const deptOptions =
    activeLang === "en"
      ? Object.keys(departmentMap.enToMk)
      : activeLang === "mk"
      ? Object.values(departmentMap.enToMk)
      : Object.values(departmentMap.enToSq);
  const deptValue = department[activeLang];

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
      <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-3xl relative max-h-[90vh] overflow-y-auto font-montserrat">
        <h2 className="text-2xl font-semibold mb-6 text-brand-800 text-center">
          {t(`forms.createCareer`)}
        </h2>

        <LanguageTabs active={activeLang} onChange={setActiveLang} />

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block font-medium mb-1">{t('forms.vacancy')} ({langSuffix})</label>
            <input
              type="text"
              value={vacancy[activeLang]}
              onChange={(e) => setField(setVacancy, e.target.value)}
              className="w-full border border-gray-300 p-2 rounded focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400 transition-colors"
              required={activeLang !== "sq"}
            />
          </div>

          <div>
            <label className="block font-medium mb-1">{t('forms.shortDescription')} ({langSuffix})</label>
            <input
              type="text"
              value={shortDescription[activeLang]}
              onChange={(e) => setField(setShortDescription, e.target.value)}
              className="w-full border border-gray-300 p-2 rounded focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400 transition-colors"
              required={activeLang !== "sq"}
            />
          </div>

          <div>
            <label className="block font-medium mb-1">{t('forms.description')} ({langSuffix})</label>
            <ReactQuill
              ref={quillRef}
              key={`desc-${activeLang}`}
              value={description[activeLang]}
              onChange={(value) => setField(setDescription, value)}
              theme="snow"
              className="h-48"
            />
          </div>
          <br />
          <br />

          <div>
            <label className="block font-medium mb-1">{t('forms.department')} ({langSuffix})</label>
            <select
              value={deptValue}
              onChange={(e) => onDepartmentChange(e.target.value)}
              className="w-full p-2 border rounded"
            >
              {deptOptions.map((dep) => (
                <option key={dep} value={dep}>{dep}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <label className="font-medium">{t('forms.active')} ({langSuffix})</label>
            <input
              type="checkbox"
              checked={active[activeLang]}
              onChange={() => setActive((prev) => ({ ...prev, [activeLang]: !prev[activeLang] }))}
            />
          </div>

          <FormActionButtons
            onClose={handleCancel}
            onSubmit={handleSubmit}
            edit={false}
            disabled={!isFormValid()}
          />
        </form>
      </div>
    </div>
  );
};

export default CareerModal;
